import {
  EControlSettingType,
  EDimensionKey,
  EReportControlId,
} from '~/presentation/reports/common/type';
import { checkHasControlFilters, getOrderJourneySource } from '~/modules/order-analytic/helpers';
import { useControlSettingStore } from '~/presentation/reports/common/ReportFilter/stores';
import { isMainColumn, ReportChart } from '~/presentation/reports/common/Visualization';
import { GModalOrderJourney, useGModalOrderJourney } from '~/presentation/common';
import { useFetchReportOrderListing } from '../hooks/api/report-order-listing';
import { useReportContext, useReportControlContext } from '../contexts';
import { usePageAnalyticFilterStore } from '~/modules/page-analytic';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { AnalyticInterval } from '~/modules/analytics/types';
import { AnalyticsSdkProvider } from '~/modules/analytics';
import { useReportTable } from '../hooks/useReportTable';
import { REPORT_FILTER_WIDTH } from '../constants';
import { useReportControlStore } from '../stores';
import { getDimensionSettingUIs } from '../utils';
import { ReportTable } from '@gem-sdk/analytics';
import { useViewAllStoreOrders } from '../hooks';
import { useTranslation } from 'react-i18next';
import { BlockStack } from '@shopify/polaris';
import { GBoxCard } from '~/presentation/ui';
import { useMemo } from 'react';

export const Visualization = () => {
  const { t } = useTranslation();

  const mode = usePageAnalyticFilterStore((state) => state.analyticMode);
  const openReportControl = useReportControlStore((state) => state.openReportControl);
  const reportControlData = useReportControlStore((state) => state.reportControlData);
  const addReportControlData = useReportControlStore((state) => state.addReportControlData);
  const addControlSettingUIs = useControlSettingStore((state) => state.addControlSettingUIs);

  const { metrics, chartType, metricKey, dimensions, orderBy, onChangeOrderBy } =
    useReportControlContext();
  const { isLoading, tableData, totalStoreOrders } = useReportContext();

  const { orderList, onClickOrderCell, isFetching } = useFetchReportOrderListing();
  const { summaryTitle } = useReportTable({ dimensions, tableData });

  const { handleOpenModalOrderJourney } = useGModalOrderJourney();
  const { handleOpenPageShopifyInNewTab } = useLocationHandlers();
  const { handleViewAllStoreOrders } = useViewAllStoreOrders();

  const journeySource = getOrderJourneySource(reportControlData);
  const isHasControlFilters = checkHasControlFilters(reportControlData);

  const handleViewOrderJourney = (orderId: string, gxId?: string) => {
    handleOpenModalOrderJourney({ id: orderId, orderId, gxId });
  };

  const handleViewOrderDetails = (orderId: string) => {
    handleOpenPageShopifyInNewTab(`orders/${orderId}`);
  };

  const addTimeDimension = () => {
    const data = [
      {
        controlID: EDimensionKey.TIME,
        value: AnalyticInterval.DAY.toLocaleLowerCase(),
      },
      {
        controlID: EReportControlId.DIMENSIONS_FILTER,
        value: [EDimensionKey.TIME],
      },
    ];
    addReportControlData(data);

    addControlSettingUIs(
      EControlSettingType.REPORT,
      EReportControlId.DIMENSIONS_FILTER,
      getDimensionSettingUIs([EDimensionKey.TIME]),
    );
  };

  const dimensionsByColumn = useMemo(() => {
    return tableData.columns
      .filter(
        (column) =>
          dimensions.includes(column.name) ||
          (isMainColumn(column.name) && !metrics.includes(column.name)),
      )
      .map((column) => column.name);
  }, [dimensions, tableData.columns, metrics]);

  return (
    <AnalyticsSdkProvider>
      <div
        className="transition-all duration-500 ease-in-out"
        style={{ width: openReportControl ? `calc(100% - ${REPORT_FILTER_WIDTH} - 16px)` : '100%' }}
      >
        <BlockStack gap="400">
          <GBoxCard padding="0" borderColor="border">
            <ReportChart
              data={tableData}
              metricKey={metricKey}
              chartType={chartType}
              dimensions={dimensions}
              addTimeDimension={addTimeDimension}
              isLoading={isLoading}
            />
            <ReportTable
              isLoading={isLoading}
              metrics={metrics}
              dimensions={dimensionsByColumn}
              data={tableData}
              activeColumn={metricKey}
              orderBy={orderBy}
              onChangeOrderBy={onChangeOrderBy}
              itemCount={totalStoreOrders}
              showViewAllButton={isHasControlFilters}
              onViewAllStoreOrders={handleViewAllStoreOrders}
              onViewOrderJourney={handleViewOrderJourney}
              onViewOrderDetails={handleViewOrderDetails}
              options={{
                summary: {
                  content: summaryTitle.currentTitle,
                  previousContent: summaryTitle.compareTitle,
                  showPrevious: true,
                },
              }}
              orderCellProps={{
                isFetching,
                orderList,
                onClickOrderCell,
              }}
            />
            <GModalOrderJourney
              title={t('Order journey')}
              mode={mode}
              journeySource={journeySource}
            />
          </GBoxCard>
        </BlockStack>
      </div>
    </AnalyticsSdkProvider>
  );
};
